import React from 'react';
import { motion } from 'framer-motion';
import { FiServer, FiShield, FiAlertTriangle, FiCheckCircle, FiTrendingUp, FiSettings, FiCloud } from 'react-icons/fi';
import ScrollHeader from '../components/ScrollHeader';
import MotionDiv from '../components/MotionDiv';

const InfrastructureSecurity = () => {
  const currentStats = [
    { label: "Infrastructure Scans", value: "28", description: "infrastructure assessments completed", icon: <FiServer /> },
    { label: "Cloud Resources", value: "134", description: "cloud resources analyzed", icon: <FiCloud /> },
    { label: "Security Issues", value: "45", description: "infrastructure vulnerabilities", icon: <FiAlertTriangle /> },
    { label: "Last Scan", value: "6", description: "hours ago", icon: <FiTrendingUp /> }
  ];

  const coreFeatures = [
    {
      title: "Cloud Infrastructure Assessment",
      icon: <FiCloud />,
      features: [
        "Multi-Cloud Support: AWS, Azure, Google Cloud Platform security assessment",
        "Resource Discovery: Automated discovery of cloud resources and services",
        "Configuration Analysis: Security configuration assessment and validation",
        "Compliance Checking: Cloud security compliance and best practices validation"
      ]
    },
    {
      title: "Container & Orchestration Security",
      icon: <FiServer />,
      features: [
        "Container Scanning: Docker and container image security assessment",
        "Kubernetes Security: Kubernetes cluster and workload security analysis",
        "Registry Scanning: Container registry security and vulnerability scanning",
        "Runtime Security: Container runtime security monitoring and analysis"
      ]
    },
    {
      title: "Network & Infrastructure Security",
      icon: <FiShield />,
      features: [
        "Network Segmentation: Network security and segmentation analysis",
        "Firewall Assessment: Firewall rules and security policy validation",
        "SSL/TLS Analysis: Certificate and encryption configuration assessment",
        "Infrastructure Hardening: System hardening and security configuration review"
      ]
    }
  ];

  const cloudPlatforms = [
    { name: "Amazon Web Services (AWS)", description: "EC2, S3, RDS, Lambda, VPC, IAM security assessment" },
    { name: "Microsoft Azure", description: "Virtual Machines, Storage, SQL Database, App Service security" },
    { name: "Google Cloud Platform", description: "Compute Engine, Cloud Storage, Cloud SQL, GKE security" },
    { name: "Kubernetes", description: "Container orchestration security and cluster assessment" },
    { name: "Docker", description: "Container security and image vulnerability scanning" },
    { name: "On-Premises", description: "Traditional infrastructure and hybrid cloud security" }
  ];

  const securityAssessments = [
    {
      category: "Cloud Security Configuration",
      assessments: [
        "IAM Policies: Identity and Access Management configuration review",
        "Storage Security: Object storage and database security assessment",
        "Network Security: VPC, security groups, and network ACL analysis",
        "Encryption: Data encryption at rest and in transit validation"
      ]
    },
    {
      category: "Container Security",
      assessments: [
        "Image Vulnerabilities: Container image vulnerability scanning",
        "Runtime Security: Container runtime security and behavior analysis",
        "Registry Security: Container registry access control and security",
        "Orchestration Security: Kubernetes and container orchestration security"
      ]
    },
    {
      category: "Infrastructure Hardening",
      assessments: [
        "System Configuration: Operating system hardening and configuration",
        "Service Security: Service configuration and security assessment",
        "Patch Management: System patching and vulnerability management",
        "Access Controls: System access control and privilege management"
      ]
    },
    {
      category: "Compliance & Governance",
      assessments: [
        "Regulatory Compliance: SOC 2, ISO 27001, PCI DSS compliance assessment",
        "Security Policies: Security policy implementation and enforcement",
        "Audit Logging: Security event logging and monitoring configuration",
        "Incident Response: Incident response capability and readiness assessment"
      ]
    }
  ];

  const vulnerabilityCategories = [
    { category: "Critical Infrastructure Flaws", description: "Severe infrastructure vulnerabilities requiring immediate attention", color: "text-red-500" },
    { category: "High-Risk Misconfigurations", description: "Significant security misconfigurations with high impact", color: "text-orange-500" },
    { category: "Medium-Risk Issues", description: "Moderate infrastructure security issues requiring remediation", color: "text-yellow-500" },
    { category: "Low-Risk Findings", description: "Minor security concerns and best practice violations", color: "text-blue-500" }
  ];

  const integrationCapabilities = [
    {
      title: "Cloud Provider Integration",
      integrations: [
        "AWS APIs: Direct integration with AWS services and APIs",
        "Azure Resource Manager: Integration with Azure management APIs",
        "Google Cloud APIs: Native integration with GCP services",
        "Multi-Cloud Management: Unified security assessment across cloud providers"
      ]
    },
    {
      title: "DevOps & CI/CD Integration",
      integrations: [
        "Infrastructure as Code: Terraform, CloudFormation, ARM template scanning",
        "CI/CD Pipelines: Automated infrastructure security testing",
        "Container Registries: Integration with Docker Hub, ECR, ACR, GCR",
        "Kubernetes Integration: Native Kubernetes cluster security assessment"
      ]
    },
    {
      title: "Security & Monitoring Tools",
      integrations: [
        "SIEM Integration: Security event correlation and monitoring",
        "Cloud Security Posture Management: CSPM tool integration",
        "Vulnerability Management: Integration with vulnerability scanning platforms",
        "Compliance Tools: Integration with compliance management platforms"
      ]
    }
  ];

  const bestPractices = [
    {
      category: "Cloud Security",
      practices: [
        "Least Privilege: Implement principle of least privilege for cloud access",
        "Multi-Factor Authentication: Enable MFA for all cloud accounts",
        "Encryption: Encrypt data at rest and in transit",
        "Network Segmentation: Implement proper network segmentation and isolation",
        "Regular Auditing: Conduct regular security audits and assessments"
      ]
    },
    {
      category: "Container Security",
      practices: [
        "Image Scanning: Scan container images for vulnerabilities before deployment",
        "Runtime Security: Monitor container runtime behavior and security",
        "Least Privilege: Run containers with minimal privileges and capabilities",
        "Network Policies: Implement Kubernetes network policies for isolation",
        "Secret Management: Use proper secret management for container credentials"
      ]
    },
    {
      category: "Infrastructure Management",
      practices: [
        "Infrastructure as Code: Use IaC for consistent and secure deployments",
        "Automated Patching: Implement automated security patching processes",
        "Configuration Management: Maintain secure configuration baselines",
        "Monitoring: Implement comprehensive security monitoring and alerting",
        "Incident Response: Establish infrastructure security incident response procedures"
      ]
    }
  ];

  const advancedFeatures = [
    {
      title: "Automated Assessment",
      features: [
        "Continuous Monitoring: Real-time infrastructure security monitoring",
        "Policy as Code: Security policies defined and enforced as code",
        "Automated Remediation: Automated fixing of common security issues",
        "Drift Detection: Detection of configuration drift from security baselines"
      ]
    },
    {
      title: "Intelligence & Analytics",
      features: [
        "Threat Intelligence: Integration with threat intelligence feeds",
        "Risk Analytics: Advanced risk analysis and scoring",
        "Trend Analysis: Historical security trend analysis and reporting",
        "Predictive Security: Predictive analysis of potential security risks"
      ]
    }
  ];

  const complianceFrameworks = [
    { name: "SOC 2", description: "Service Organization Control 2 compliance assessment" },
    { name: "ISO 27001", description: "Information Security Management System compliance" },
    { name: "PCI DSS", description: "Payment Card Industry Data Security Standard" },
    { name: "HIPAA", description: "Health Insurance Portability and Accountability Act" },
    { name: "GDPR", description: "General Data Protection Regulation compliance" },
    { name: "NIST", description: "National Institute of Standards and Technology framework" }
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <ScrollHeader title="Infrastructure Security" />
        
        {/* Hero Section */}
        <MotionDiv delay={0.1} variant="fadeIn">
          <div className="text-center mb-12">
            <MotionDiv delay={0.15} variant="bounce">
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-primary-500/10 rounded-full">
                  <FiServer className="w-16 h-16 text-primary-500" />
                </div>
              </div>
            </MotionDiv>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-3xl mx-auto">
              The Infrastructure Security module in Ana Security provides comprehensive security assessment for cloud infrastructure, containers, and on-premises systems, ensuring secure configuration and compliance across your entire technology stack.
            </p>
            
            <MotionDiv delay={0.2} variant="scale">
              <div className="mb-8">
                <img 
                  src="/screenshots/security-modules/11_infrastructure-scan.png" 
                  alt="Infrastructure Security Dashboard"
                  className="rounded-lg border border-light-border dark:border-dark-border shadow-lg w-full max-w-4xl mx-auto hover:shadow-xl transition-shadow duration-300"
                />
              </div>
            </MotionDiv>
          </div>
        </MotionDiv>

        {/* Current Statistics */}
        <MotionDiv delay={0.3} variant="slideLeft">
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Infrastructure Security Dashboard - Current Statistics
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {currentStats.map((stat, index) => (
                <MotionDiv key={index} delay={0.4 + index * 0.1} variant="bounce" hover>
                  <div className="text-center p-6 bg-light-hover dark:bg-dark-hover rounded-lg">
                    <div className="text-3xl text-primary-500 mb-3 flex justify-center">
                      {stat.icon}
                    </div>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                      {stat.value}
                    </div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {stat.label}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {stat.description}
                    </div>
                  </div>
                </MotionDiv>
              ))}
            </div>

            <div className="bg-primary-500/5 dark:bg-primary-500/10 border border-primary-500/20 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Dashboard Features
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Multi-Cloud Assessment:</strong> Unified security assessment across cloud providers</span>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Container Security:</strong> Comprehensive container and Kubernetes security</span>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Compliance Monitoring:</strong> Continuous compliance assessment and reporting</span>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Real-time Monitoring:</strong> Continuous infrastructure security monitoring</span>
                </div>
              </div>
            </div>
          </div>
        </MotionDiv>

        {/* Core Features */}
        <MotionDiv delay={0.2}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Infrastructure Security Core Features
            </h2>
            
            <div className="space-y-8">
              {coreFeatures.map((feature, index) => (
                <div key={index} className="border border-light-border dark:border-dark-border rounded-lg p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-2xl text-primary-500">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {feature.title}
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {feature.features.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-700 dark:text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Supported Platforms */}
        <MotionDiv delay={0.3}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Supported Cloud Platforms & Infrastructure
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Comprehensive security assessment across all major cloud platforms and infrastructure types:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {cloudPlatforms.map((platform, index) => (
                <div key={index} className="p-4 bg-light-hover dark:bg-dark-hover rounded-lg">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    {platform.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {platform.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Security Assessments */}
        <MotionDiv delay={0.4}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Infrastructure Security Assessments
            </h2>
            
            <div className="space-y-8">
              {securityAssessments.map((category, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {category.category}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {category.assessments.map((assessment, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-light-hover dark:bg-dark-hover rounded-lg">
                        <FiShield className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{assessment}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Vulnerability Categories */}
        <MotionDiv delay={0.5}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Infrastructure Vulnerability Categories (45 Issues)
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {vulnerabilityCategories.map((category, index) => (
                <div key={index} className="p-4 bg-light-hover dark:bg-dark-hover rounded-lg">
                  <h3 className={`font-semibold mb-2 ${category.color}`}>
                    {category.category}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {category.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="bg-primary-500/5 dark:bg-primary-500/10 border border-primary-500/20 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Common Infrastructure Security Issues
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-2">
                  <FiAlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Misconfigurations:</strong> Cloud service and security misconfigurations</span>
                </div>
                <div className="flex items-start gap-2">
                  <FiAlertTriangle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Access Control:</strong> Overprivileged access and weak authentication</span>
                </div>
                <div className="flex items-start gap-2">
                  <FiAlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Network Security:</strong> Insecure network configurations and exposure</span>
                </div>
                <div className="flex items-start gap-2">
                  <FiAlertTriangle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Compliance Gaps:</strong> Regulatory compliance violations and gaps</span>
                </div>
              </div>
            </div>
          </div>
        </MotionDiv>

        {/* Compliance Frameworks */}
        <MotionDiv delay={0.6}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Supported Compliance Frameworks
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Comprehensive compliance assessment against major regulatory frameworks:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {complianceFrameworks.map((framework, index) => (
                <div key={index} className="p-4 bg-light-hover dark:bg-dark-hover rounded-lg">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    {framework.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {framework.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Integration Capabilities */}
        <MotionDiv delay={0.7}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Integration Capabilities
            </h2>
            
            <div className="space-y-8">
              {integrationCapabilities.map((category, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {category.title}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {category.integrations.map((integration, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-light-hover dark:bg-dark-hover rounded-lg">
                        <FiSettings className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{integration}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Advanced Features */}
        <MotionDiv delay={0.8}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Advanced Features
            </h2>
            
            <div className="space-y-8">
              {advancedFeatures.map((feature, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {feature.title}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {feature.features.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-light-hover dark:bg-dark-hover rounded-lg">
                        <FiShield className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Best Practices */}
        <MotionDiv delay={0.9}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Infrastructure Security Best Practices
            </h2>
            
            <div className="space-y-8">
              {bestPractices.map((category, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {category.category}
                  </h3>
                  <div className="space-y-3">
                    {category.practices.map((practice, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{practice}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Call to Action */}
        <MotionDiv delay={1.0}>
          <div className="text-center bg-primary-500/5 dark:bg-primary-500/10 border border-primary-500/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Ready to Secure Your Infrastructure?
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Start using Infrastructure Security to assess and secure your cloud, container, and on-premises infrastructure.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a 
                href="/ana-actions" 
                className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
              >
                <FiCheckCircle className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Use ANA Actions</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Automatically fix infrastructure issues</p>
              </a>
              
              <a 
                href="/dashboard" 
                className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
              >
                <FiTrendingUp className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">View Dashboard</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Monitor your infrastructure security</p>
              </a>
              
              <a 
                href="/integrations" 
                className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
              >
                <FiSettings className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Setup Integrations</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Connect your cloud platforms</p>
              </a>
            </div>
          </div>
        </MotionDiv>
      </motion.div>
    </div>
  );
};

export default InfrastructureSecurity;
